import { useQuery } from '@tanstack/react-query';
import type { SelectOption } from '@chopchop-se/ui';
import type { ArticleCategory, Supplier, Unit } from '@warehouse/shared';
import { useTranslation } from 'react-i18next';

import { listCategories, listSuppliers, listUnits } from './api';

/**
 * The three lookups every master-data form needs.
 *
 * They are small, bounded registers (tens of rows), so one cached query each
 * beats a searchable remote select: the article form must be usable on a
 * warehouse tablet with a flaky AP, and a select that cannot populate is a form
 * that cannot be saved. `staleTime` keeps them out of the way while the user
 * fills the form in.
 */

const LOOKUP_STALE_MS = 5 * 60_000;
const LOOKUP_PAGE = { page: 1, pageSize: 500 };

export function useUnits(): { units: Unit[]; options: SelectOption[] } {
  const { data } = useQuery({
    queryKey: ['master-data', 'lookup', 'units'],
    queryFn: () => listUnits(LOOKUP_PAGE),
    staleTime: LOOKUP_STALE_MS,
  });
  const units = data?.items ?? [];
  return {
    units,
    options: units.map((unit) => ({ value: unit.id, label: `${unit.code} — ${unit.name}` })),
  };
}

export function useCategories(): { categories: ArticleCategory[]; options: SelectOption[] } {
  const { data } = useQuery({
    queryKey: ['master-data', 'lookup', 'categories'],
    queryFn: () => listCategories(LOOKUP_PAGE),
    staleTime: LOOKUP_STALE_MS,
  });
  const categories = data?.items ?? [];
  return {
    categories,
    options: categories.map((category) => ({ value: category.id, label: category.name })),
  };
}

export function useSuppliers(): { suppliers: Supplier[]; options: SelectOption[] } {
  const { data } = useQuery({
    queryKey: ['master-data', 'lookup', 'suppliers'],
    queryFn: () => listSuppliers({ ...LOOKUP_PAGE, sort: 'name' }),
    staleTime: LOOKUP_STALE_MS,
  });
  const suppliers = data?.items ?? [];
  return {
    suppliers,
    options: suppliers.map((supplier) => ({
      value: supplier.id,
      label: supplier.code ? `${supplier.name} (${supplier.code})` : supplier.name,
    })),
  };
}

/** `Alla` / `Ja` / `Nej` triples for the boolean list filters. */
export function useBooleanFilterOptions(): SelectOption[] {
  const { t } = useTranslation('common');
  return [
    { value: '', label: t('common.all') },
    { value: 'true', label: t('common.yes') },
    { value: 'false', label: t('common.no') },
  ];
}
